import {
  auditFieldMap,
  adminModuleFieldMap,
  ADMIN_ROLES_WITH_MODULES,
  ignoredFields,
} from "./auditFieldMap.js";
import { formatCapitalize } from "./formatCapitalize.js";
import {
  serializeCampaignAudience,
  mergeCampaignAudienceRecord,
  normalizeCampaignScheduleDetails,
} from "./campaignAudiencePayload.js";
import {
  hasDisplayableCampaignAudience,
  getCampaignAudienceTypeLabel,
  getCampaignAudienceDetailItems,
} from "./campaignAudienceAudit.js";

export {
  serializeCampaignAudience,
  mergeCampaignAudienceRecord,
  normalizeCampaignScheduleDetails,
  hasDisplayableCampaignAudience,
  getCampaignAudienceTypeLabel,
  getCampaignAudienceDetailItems,
};

export const ADMIN_MODULES_FIELD = "modules";
export const PARENT_PHONE_NOTIFICATIONS_FIELD = "phoneNotifications";

export const isAdminRoleWithModules = (role) =>
  ADMIN_ROLES_WITH_MODULES.includes(String(role || "").toUpperCase());

export const normalizeAdminModuleValue = (value) => {
  if (value === true || value === "TRUE" || value === "true") return true;
  if (value === false || value === "FALSE" || value === "false") return false;
  return Boolean(value);
};

export const getAdminRecordRole = (record) => record?.privilege?.role;

export const shouldShowAdminModules = (record) =>
  isAdminRoleWithModules(getAdminRecordRole(record));

export const isAdminModuleField = (field) =>
  Object.prototype.hasOwnProperty.call(adminModuleFieldMap, field);

export const getAdminFieldLabel = (field) => {
  if (field === ADMIN_MODULES_FIELD) return "Modules";
  return auditFieldMap.admins?.[field] || adminModuleFieldMap[field] || field;
};

export const isAdminModuleValueChanged = (field, before, after) => {
  const showBefore = shouldShowAdminModules(before);
  const showAfter = shouldShowAdminModules(after);
  if (!showBefore && !showAfter) return false;
  if (showBefore && !showAfter) return false;
  if (!showBefore && showAfter) {
    const modules = after?.privilege?.modules;
    return (
      Boolean(modules) && Object.prototype.hasOwnProperty.call(modules, field)
    );
  }
  const beforeVal = normalizeAdminModuleValue(
    before?.privilege?.modules?.[field],
  );
  const afterVal = normalizeAdminModuleValue(
    after?.privilege?.modules?.[field],
  );
  return beforeVal !== afterVal;
};

export const isAdminModulesChanged = (before, after) => {
  if (!shouldShowAdminModules(before) && !shouldShowAdminModules(after)) {
    return false;
  }
  if (shouldShowAdminModules(before) && !shouldShowAdminModules(after)) {
    return false;
  }
  if (!shouldShowAdminModules(before) && shouldShowAdminModules(after)) {
    return Object.keys(after?.privilege?.modules || {}).length > 0;
  }
  return Object.keys(adminModuleFieldMap).some((key) =>
    isAdminModuleValueChanged(key, before, after),
  );
};

export const getAdminModuleKeysForDisplay = (before, after, actionType) => {
  if (actionType === "CREATE") {
    return Object.keys(after?.privilege?.modules || {}).filter((key) =>
      isAdminModuleField(key),
    );
  }
  if (actionType === "DELETE") {
    return Object.keys(before?.privilege?.modules || {}).filter((key) =>
      isAdminModuleField(key),
    );
  }
  if (!shouldShowAdminModules(before) && shouldShowAdminModules(after)) {
    return Object.keys(after?.privilege?.modules || {}).filter((key) =>
      isAdminModuleField(key),
    );
  }
  return Object.keys(adminModuleFieldMap).filter((key) =>
    isAdminModuleValueChanged(key, before, after),
  );
};

export const getAdminAuditFieldKeys = (record) => {
  const base = Object.keys(auditFieldMap.admins || {});
  if (!shouldShowAdminModules(record)) return base;
  return [...base, ADMIN_MODULES_FIELD];
};

export const getAdminAuditFieldKeysForUpdate = (before, after) => {
  const base = Object.keys(auditFieldMap.admins || {});
  if (shouldShowAdminModules(before) || shouldShowAdminModules(after)) {
    return [...base, ADMIN_MODULES_FIELD];
  }
  return base;
};

export const hasDisplayableAdminFieldValue = (record, field) => {
  if (!record) return false;
  if (field === ADMIN_MODULES_FIELD) {
    if (!shouldShowAdminModules(record)) return false;
    return Object.keys(record?.privilege?.modules || {}).length > 0;
  }
  if (field === "role") return Boolean(record?.privilege?.role);
  if (field === "firstName") return Boolean(record?.admin?.firstName);
  if (field === "lastName") return Boolean(record?.admin?.lastName);
  if (field === "email") return Boolean(record?.admin?.email);
  return false;
};

export const getTripTypeName = (record) =>
  record?.triptypeName ?? record?.tripTypeName ?? null;

export const normalizeInstitutionIds = (ids) =>
  (Array.isArray(ids) ? ids : [])
    .filter((id) => id != null && id !== "")
    .map((id) => String(id));

export const serializeInstitutionIds = (value) =>
  JSON.stringify([...normalizeInstitutionIds(value)].sort());

export const flattenAuditRecord = (data, module) => {
  if (!data) return {};
  if (module === "admins") {
    const flat = {
      ...(data.admin || {}),
      role: data.privilege?.role,
    };
    if (shouldShowAdminModules(data)) {
      const modules = data.privilege?.modules || {};
      const normalized = {};
      for (const key of Object.keys(adminModuleFieldMap)) {
        if (Object.prototype.hasOwnProperty.call(modules, key)) {
          normalized[key] = normalizeAdminModuleValue(modules[key]);
        }
      }
      flat.modules = normalized;
    }
    return flat;
  }
  if (module === "drivers" && data.passwordChange !== true) {
    return { ...data, passwordChange: false };
  }
  if (module === "trips") {
    return {
      ...data,
      triptypeName: getTripTypeName(data),
    };
  }
  return data;
};

export const getAuditRecordForAction = (actionType, changes) => {
  if (actionType === "DELETE") {
    return changes?.before || changes?.after;
  }
  if (actionType === "CREATE") {
    return changes?.after || changes?.before;
  }
  return changes?.after || changes?.before;
};

const getScheduleTripName = (record) =>
  record?.tripDetails?.tripName || record?.tripName || "";

export const normalizeAffectedStudents = (students) => {
  if (!Array.isArray(students)) return [];
  return students
    .map((student) => ({
      childId: student?.childId != null ? String(student.childId) : "",
      name: student?.name ?? "",
      status: student?.status ?? "",
    }))
    .sort((a, b) => a.childId.localeCompare(b.childId));
};

export const serializeAffectedStudents = (students) =>
  JSON.stringify(normalizeAffectedStudents(students));

export const normalizeAssociatedChilds = (children) => {
  if (!Array.isArray(children)) return [];
  return children
    .map((child) => ({
      childId: child?.childId != null ? String(child.childId) : "",
      childName: child?.childName ?? child?.name ?? "",
      tripSession: child?.tripSession ?? "none",
    }))
    .sort((a, b) => a.childId.localeCompare(b.childId));
};

export const serializeAssociatedChilds = (children) =>
  JSON.stringify(normalizeAssociatedChilds(children));

export const normalizeTripSession = (session) => {
  if (session == null || session === "") return "none";
  return String(session).toLowerCase();
};

export const partitionAssociatedChildsChanges = (beforeChildren, afterChildren) => {
  const before = normalizeAssociatedChilds(beforeChildren);
  const after = normalizeAssociatedChilds(afterChildren);

  const beforeById = new Map(
    before.filter((child) => child.childId).map((child) => [child.childId, child]),
  );
  const afterById = new Map(
    after.filter((child) => child.childId).map((child) => [child.childId, child]),
  );

  const tripSessionOnly = [];
  const beforeForList = [];
  const afterForList = [];

  const allIds = new Set([...beforeById.keys(), ...afterById.keys()]);

  for (const childId of allIds) {
    const beforeChild = beforeById.get(childId);
    const afterChild = afterById.get(childId);

    if (!beforeChild && afterChild) {
      afterForList.push(afterChild);
    } else if (beforeChild && !afterChild) {
      beforeForList.push(beforeChild);
    } else if (beforeChild && afterChild) {
      const sessionChanged =
        normalizeTripSession(beforeChild.tripSession) !==
        normalizeTripSession(afterChild.tripSession);
      const nameChanged = beforeChild.childName !== afterChild.childName;

      if (sessionChanged && !nameChanged) {
        tripSessionOnly.push({
          childId,
          childName: afterChild.childName || beforeChild.childName,
          beforeTripSession: beforeChild.tripSession,
          afterTripSession: afterChild.tripSession,
        });
      } else if (nameChanged || sessionChanged) {
        beforeForList.push(beforeChild);
        afterForList.push(afterChild);
      }
    }
  }

  return {
    tripSessionOnly,
    beforeForList,
    afterForList,
    hasListChanges: beforeForList.length > 0 || afterForList.length > 0,
  };
};

export const isAssociatedChildsTripSessionOnlyUpdate = (before, after) => {
  const { tripSessionOnly, hasListChanges } = partitionAssociatedChildsChanges(
    before?.associatedChilds,
    after?.associatedChilds,
  );
  return tripSessionOnly.length > 0 && !hasListChanges;
};

export const formatTripSessionLabel = (session) => {
  if (!session || session === "none") return "All";
  const normalized = String(session).toLowerCase();
  const labels = {
    am: "AM",
    pm: "PM",
    eca: "ECA",
    "5pm": "5PM",
    "7pm": "7PM",
  };
  return labels[normalized] || formatCapitalize(session);
};

export const isTripScheduleStudentsUpdate = (
  module,
  actionType,
  before,
  after,
) =>
  module === "schedules" &&
  actionType === "UPDATE" &&
  !before?.childName &&
  !after?.childName &&
  (Array.isArray(before?.affectedStudents) ||
    Array.isArray(after?.affectedStudents));

export const getAuditRecordName = (module, record) => {
  if (!record) return "";

  switch (module) {
    case "drivers":
      return record.name || record.preferredName || "";
    case "vehicles":
      return record.vehicleNumber || record.busNumber || "";
    case "parents":
      return (
        record.fullName ||
        [record.firstName, record.lastName].filter(Boolean).join(" ")
      );
    case "childs":
      return (
        record.fullName ||
        [record.firstName, record.lastName].filter(Boolean).join(" ")
      );
    case "campaigns":
    case "stops":
      return record.name || "";
    case "deeplinks":
      return record.label || "";
    case "contents":
      return record.title || "";
    case "trips":
      return record.name || record.tripName || "";
    case "admins":
      return (
        record.admin?.fullName ||
        [record.admin?.firstName, record.admin?.lastName]
          .filter(Boolean)
          .join(" ")
      );
    case "families":
      return record.familyId || "";
    case "schedules": {
      if (record.childName) return record.childName;
      const tripName = getScheduleTripName(record);
      if (tripName) return tripName;
      return record.schedules?.[0]?.scheduleName || "";
    }
    default:
      return "";
  }
};

export const getAuditRecordLabel = (module, record) => {
  switch (module) {
    case "vehicles":
      return record?.vehicleNumber
        ? "Registration Number"
        : record?.busNumber
          ? "Bus Number"
          : "Registration Number";
    case "families":
      return "Family ID";
    case "schedules":
      if (record?.childName) return "Child";
      if (getScheduleTripName(record)) return "Trip";
      return "Schedule";
    case "admins":
      return "Administrator";
    case "drivers":
    case "parents":
    case "childs":
      return "Name";
    case "campaigns":
      return "Campaign Name";
    case "deeplinks":
      return "Label";
    case "contents":
      return "Title";
    case "trips":
    case "stops":
      return "Name";
    default:
      return "Record";
  }
};

export const isAuditFieldChanged = (module, field, before, after) => {
  const beforeFlat = flattenAuditRecord(before, module);
  const afterFlat = flattenAuditRecord(after, module);

  if (module === "drivers" && field === "passwordChange") {
    return afterFlat.passwordChange === true;
  }

  if (module === "campaigns" && field === "lastTriggered") {
    const afterTime = after?.lastTriggered;
    const beforeTime = before?.lastTriggered;
    return Boolean(afterTime) && afterTime !== beforeTime;
  }

  if (module === "campaigns" && field === "targetAudience") {
    return (
      serializeCampaignAudience(before) !== serializeCampaignAudience(after)
    );
  }

  if (module === "admins" && field === ADMIN_MODULES_FIELD) {
    return isAdminModulesChanged(before, after);
  }

  if (module === "deeplinks" && field === "institutionIds") {
    return (
      serializeInstitutionIds(before?.institutionIds) !==
      serializeInstitutionIds(after?.institutionIds)
    );
  }

  if (module === "schedules" && field === "affectedStudents") {
    return (
      serializeAffectedStudents(before?.affectedStudents) !==
      serializeAffectedStudents(after?.affectedStudents)
    );
  }

  if (module === "stops" && field === "associatedChilds") {
    return (
      serializeAssociatedChilds(before?.associatedChilds) !==
      serializeAssociatedChilds(after?.associatedChilds)
    );
  }

  if (module === "parents" || module === "childs") {
    const beforeHas = Object.prototype.hasOwnProperty.call(beforeFlat, field);
    const afterHas = Object.prototype.hasOwnProperty.call(afterFlat, field);
    if (!beforeHas || !afterHas) return false;
  }

  return (
    JSON.stringify(beforeFlat[field]) !== JSON.stringify(afterFlat[field])
  );
};

export const isCampaignTriggeredUpdate = (
  module,
  actionType,
  before,
  after,
) =>
  module === "campaigns" &&
  actionType === "UPDATE" &&
  isAuditFieldChanged("campaigns", "lastTriggered", before, after);

export const isParentPhoneNotificationsUpdate = (module, actionType, after) =>
  module === "parents" &&
  actionType === "UPDATE" &&
  after != null &&
  Object.prototype.hasOwnProperty.call(after, "fcmToken");

export const getAuditChangedFieldLabels = (module, before, after) => {
  const fields = auditFieldMap[module] || {};
  const ignored = ignoredFields[module]?.UPDATE || [];

  if (isCampaignTriggeredUpdate(module, "UPDATE", before, after)) {
    return fields.lastTriggered ? [fields.lastTriggered] : [];
  }

  if (isParentPhoneNotificationsUpdate(module, "UPDATE", after)) {
    return ["Phone Notifications"];
  }

  if (module === "admins") {
    return getAdminAuditFieldKeysForUpdate(before, after)
      .filter((key) => !ignored.includes(key))
      .filter((key) => isAuditFieldChanged(module, key, before, after))
      .map((key) => getAdminFieldLabel(key));
  }

  // Schedules: the total count is folded into the "Affected Students" view in the
  // modal, so don't list "Students Scheduled" separately when students changed.
  const foldTotalIntoAffected =
    module === "schedules" &&
    isAuditFieldChanged(module, "affectedStudents", before, after);

  return Object.keys(fields)
    .filter((key) => !ignored.includes(key))
    .filter((key) => !(foldTotalIntoAffected && key === "totalStudents"))
    .filter((key) => isAuditFieldChanged(module, key, before, after))
    .map((key) => {
      if (
        module === "stops" &&
        key === "associatedChilds" &&
        isAssociatedChildsTripSessionOnlyUpdate(before, after)
      ) {
        return "Trip Session";
      }
      return fields[key];
    });
};

export const getAuditDetailParts = (row) => {
  const { module, actionType, changes } = row;

  const record = getAuditRecordForAction(actionType, changes);

  const primary = getAuditRecordName(module, record) || "—";

  if (actionType === "UPDATE") {
    const changedLabels = getAuditChangedFieldLabels(
      module,
      changes?.before,
      changes?.after,
    );
    return {
      primary,
      secondary:
        changedLabels.length > 0 ? changedLabels.join(", ") : null,
    };
  }

  return { primary, secondary: null };
};

export const getAuditDetailsText = (row) => {
  const { primary, secondary } = getAuditDetailParts(row);
  return secondary ? `${primary} · ${secondary}` : primary;
};
